import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCommandManager } from '../../state/commandStore';
import { dynamicOwnsKeyEvent } from '../../state/useDynamicInputStore';
import { useAppStore } from '../../state/store';
// Direct import: the snapping barrel also exports the drei-based SnapIndicator,
// which would pin drei/fiber into this eagerly-mounted component's chunk.
import { useSnapStore } from '../../tools/snapping/SnapManager';
import { useViewStore } from '../../state/commandAuxStores';
import { cycleIsoPlane } from '../../core/drafting/isoPlane';
import { useGumballStore } from '../../state/useGumballStore';

/** Routes whose workbenches map F1 to the shared Ortho toggle. */
const ORTHO_F1_ROUTES = ['/2d', '/3d', '/bim'];
/** Routes where the classic drafting F-key band is active. */
const CAD_FKEY_ROUTES = ['/2d', '/3d'];

export function KeyboardListener() {
    const location = useLocation();
    // Route read through a ref so the mount-only keydown listener (empty deps)
    // always sees the current workbench without rebinding on navigation.
    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;

    const getCommandByShortcutRef = useRef(useCommandManager.getState().getCommandByShortcut);
    const hasCommandWithPrefixRef = useRef(useCommandManager.getState().hasCommandWithPrefix);
    const executeCommandRef = useRef(useCommandManager.getState().executeCommand);
    const bufferRef = useRef('');
    const bufferTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        const clearBufferTimeout = () => {
            if (bufferTimeoutRef.current) {
                clearTimeout(bufferTimeoutRef.current);
                bufferTimeoutRef.current = null;
            }
        };

        const resetBuffer = () => {
            bufferRef.current = '';
            clearBufferTimeout();
        };

        /**
         * Resolve a shortcut to the command that may run on the CURRENT
         * route. Sketch-scoped commands are owned by SketchMode's
         * capture-phase handler, so the global listener must not fire them
         * from another workbench (their single-letter shortcuts would
         * otherwise ride the sequence buffer's timeout path).
         */
        const resolveRouteCommand = (shortcut: string) => {
            const command = getCommandByShortcutRef.current(shortcut);
            if (!command) return undefined;
            if (command.scope === 'sketch' && !pathnameRef.current.startsWith('/sketch')) {
                return undefined;
            }
            return command;
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            // Ignore if typing in an input, textarea, or contenteditable
            const target = e.target as HTMLElement | Window | null;
            // Synthetic events replayed through window (e.g. right-click
            // command completion) target `window`, which has no tagName/closest.
            const el = target && typeof (target as HTMLElement).closest === 'function'
                ? (target as HTMLElement)
                : null;
            if (el && (
                el.tagName === 'INPUT' ||
                el.tagName === 'TEXTAREA' ||
                el.isContentEditable
            )) {
                return;
            }

            // While a modal dialog owns the keyboard (focus trap), sequence
            // commands must not fire on the viewport behind it. Chakra's
            // Modal closes itself on Escape, so an early return is safe.
            if (
                (el && el.closest('[role="dialog"]')) ||
                document.activeElement?.closest('[role="dialog"]')
            ) {
                return;
            }

            // Dynamic Input (CAD/3D workbenches): the cursor tooltip owns
            // plain letters/digits/Space/Enter/Tab at the capture phase, so
            // the BIM-style sequence buffer must not double-consume them.
            if (dynamicOwnsKeyEvent(e)) {
                return;
            }

            const hasCommandWithPrefix = hasCommandWithPrefixRef.current;
            const executeCommand = executeCommandRef.current;

            // Handle modifier keys immediately
            if (e.ctrlKey || e.altKey || e.metaKey) {
                resetBuffer(); // Modifier combo cancels any pending sequence

                // Construct shortcut string like "Ctrl+S"
                const parts = [];
                if (e.ctrlKey) parts.push('Ctrl');
                if (e.altKey) parts.push('Alt');
                if (e.shiftKey) parts.push('Shift');
                if (e.metaKey) parts.push('Meta');

                // Only add the key if it's not a modifier itself
                if (!['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) {
                    parts.push(e.key.toUpperCase());
                    const shortcut = parts.join('+');

                    const command = resolveRouteCommand(shortcut);
                    if (command) {
                        e.preventDefault();
                        executeCommand(command.id);
                    }
                }
                return;
            }

            // Space confirms the pending buffer (BIM style: "T" + Space).
            // In the 3D workbench with no pending sequence, Space cycles
            // the Gumball manipulator mode (translate → rotate → scale).
            if (e.key === ' ') {
                if (bufferRef.current) {
                    const command = resolveRouteCommand(bufferRef.current);
                    if (command) {
                        e.preventDefault();
                        executeCommand(command.id);
                    }
                    resetBuffer();
                } else if (pathnameRef.current === '/3d' && useAppStore.getState().selectedIds.length > 0) {
                    e.preventDefault();
                    useGumballStore.getState().cycleMode();
                }
                return;
            }

            // Handle single keys and sequences (BIM style: W, T, WA, DR, etc.)
            // Only letters and digits feed the sequence buffer.
            if (/^[a-zA-Z0-9]$/.test(e.key)) {
                const newBuffer = bufferRef.current + e.key.toUpperCase();
                bufferRef.current = newBuffer;
                clearBufferTimeout();

                const command = resolveRouteCommand(newBuffer);
                if (command && !hasCommandWithPrefix(newBuffer)) {
                    // Unambiguous full match: run it right away.
                    resetBuffer();
                    e.preventDefault();
                    executeCommand(command.id);
                } else if (command || hasCommandWithPrefix(newBuffer)) {
                    // Either a prefix of longer sequences or an exact match that
                    // is also a prefix: wait for more keys, Space, or a timeout.
                    // On timeout, a pending exact match fires (BIM behavior).
                    bufferTimeoutRef.current = window.setTimeout(() => {
                        const pending = resolveRouteCommand(bufferRef.current);
                        resetBuffer();
                        if (pending) executeCommand(pending.id);
                    }, 2000);
                } else {
                    // Dead end: no command starts with this buffer.
                    resetBuffer();
                }
            } else if (e.key === 'Escape') {
                const hadBuffer = bufferRef.current.length > 0;
                resetBuffer();
                if (!hadBuffer) {
                    const command = resolveRouteCommand('Esc');
                    if (command) executeCommand(command.id);
                }
            } else if (e.key === 'Delete') {
                resetBuffer();
                const command = resolveRouteCommand('Delete');
                if (command) executeCommand(command.id);
            } else if (e.key === 'Enter') {
                resetBuffer();
                const command = resolveRouteCommand('Enter');
                if (command) executeCommand(command.id);
            } else if (/^F\d{1,2}$/.test(e.key)) {
                // Function keys (F1-F12): direct match, no sequence buffering
                resetBuffer();

                // Classic drafting F-key band in the CAD-family workbenches:
                // F3 osnap, F7 grid, F8 ortho, F9 grid snap, F10 polar,
                // F11 object snap tracking (Faz 4.3), F12 dynamic input.
                // F1 stays Ortho (legacy mapping below).
                if (CAD_FKEY_ROUTES.includes(pathnameRef.current)) {
                    const app = useAppStore.getState();
                    const snap = useSnapStore.getState();
                    switch (e.key) {
                        case 'F1':
                        case 'F8':
                            e.preventDefault();
                            app.toggleOrthoMode();
                            return;
                        case 'F3':
                            e.preventDefault();
                            snap.setEnabled(!snap.enabled);
                            return;
                        case 'F7':
                            e.preventDefault();
                            app.updateView({ gridVisible: !app.view.gridVisible });
                            return;
                        case 'F9':
                            e.preventDefault();
                            useSnapStore.getState().toggleGridSnap();
                            return;
                        case 'F10':
                            e.preventDefault();
                            useViewStore.getState().set('polarOn', !useViewStore.getState().polarOn);
                            return;
                        case 'F11':
                            e.preventDefault();
                            useSnapStore.getState().toggleObjectSnapTracking();
                            return;
                        case 'F5':
                            // ISOPLANE cycle (Faz 7.1): F5 rotates Left →
                            // Top → Right and arms the isodraft master.
                            e.preventDefault();
                            cycleIsoPlane();
                            return;
                        case 'F12':
                            e.preventDefault();
                            app.setInteractionOption('dynamicInput', !app.interactionOptions.dynamicInput);
                            return;
                    }
                }

                // In the 2D and 3D workbenches, F1 is repurposed as
                // the Ortho mode on/off toggle (shared store state), matching
                // the classic F-key drafting shortcut pattern.
                if (e.key === 'F1' && ORTHO_F1_ROUTES.includes(pathnameRef.current)) {
                    e.preventDefault();
                    useAppStore.getState().toggleOrthoMode();
                    return;
                }

                const command = resolveRouteCommand(e.key);
                if (command) {
                    e.preventDefault();
                    executeCommand(command.id);
                }
            } else {
                // Any other key cancels a pending sequence
                resetBuffer();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            if (bufferTimeoutRef.current) clearTimeout(bufferTimeoutRef.current);
        };
    }, []);

    // BIM workbench: Shift acts as a temporary ortho override while held
    // (classic drafting behavior). Tracked on the window so it works even
    // when focus sits on canvas elements that swallow synthetic key events.
    useEffect(() => {
        const setShift = (active: boolean) => {
            if (pathnameRef.current !== '/bim') return;
            if (useAppStore.getState().shiftOrthoActive !== active) {
                useAppStore.getState().setShiftOrthoActive(active);
            }
        };
        const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Shift') setShift(true); };
        const onKeyUp = (e: KeyboardEvent) => { if (e.key === 'Shift') setShift(false); };
        const onBlur = () => setShift(false);
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('blur', onBlur);
        };
    }, []);

    return null; // Invisible component
}
